/**
 * Pear API + session storage.
 *
 * Lifted verbatim from `main.ts` (Sprint 1) so the React shell can share one
 * implementation with the panels. Same endpoints, same localStorage keys —
 * an existing session on a device stays valid across the migration.
 */
import { forgetAll } from './uiState.ts';
import { GRACEFUL_FAILURE } from './errorCopy.ts';

/** Empty in dev (vite proxies /api and /ws); set at build time for Pages. */
export const API_BASE: string =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: Record<string, string> }).env
    ?.VITE_API_BASE) || '';


export type Role = 'A' | 'B';

export interface Member {
  memberId: string;
  displayName: string;
  role: Role;
  /** Reply-text colour key the partner picked (card 9QADJv3CdiOh) — one of
   *  shared/types REPLY_COLOUR_KEYS, absent on rows cached before it shipped
   *  (the Thread falls back to the role token until the next sync). */
  replyColour?: string | null;
}

export interface PairResult {
  token: string;
  role: Role;
  memberId: string;
  couple: { coupleId: string; code?: string };
  /** Ceremonial pairing moment (card 2NuQ8dd2JMqs): present and true ONLY on
   *  the join that completed the couple — the joiner's own "you just paired"
   *  signal, since it has no socket yet to receive the room's broadcast. */
  justPaired?: boolean;
}

const LS_TOKEN = 'pear_token';
const LS_COUPLE_ID = 'pear_couple_id';
const LS_ROLE = 'pear_role';
const LS_MEMBER_ID = 'pear_member_id';
const LS_MEMBERS_JSON = 'pear_members_json';
/** Account session (DB-backed login). Separate from the couple token: it only
 *  authorises /api/auth/*; the couple token opens the room. */
const LS_AUTH = 'pear_auth_session';
/** The unpaired couple's own invite code, kept across a reload (card
 *  iwzh8dGqPxKO). */
const LS_PENDING_CODE = 'pear_pending_code';
/** Card Pgl3yiKvsQFZ (founder email 2026-10-28): which pairing already showed
 *  the "you two are paired" ceremony. The celebration used to be in-memory
 *  only, which is correct for a reload but NOT for the APK: every cold start
 *  re-joins the room as a device with no member list, hears the welcome's
 *  `paired` frame again and replayed the moment. Keyed on the couple id, so it
 *  fires once per pairing and again if this device ever pairs with someone
 *  else. */
const LS_PAIR_NOTICE_COUPLE = 'pear_pair_notice_couple';

export function pairNoticeAlreadySeen(): boolean {
  try {
    const coupleId = localStorage.getItem(LS_COUPLE_ID);
    if (!coupleId) return false;
    return localStorage.getItem(LS_PAIR_NOTICE_COUPLE) === coupleId;
  } catch {
    return false;
  }
}

export function markPairNoticeSeen(): void {
  try {
    const coupleId = localStorage.getItem(LS_COUPLE_ID);
    if (coupleId) localStorage.setItem(LS_PAIR_NOTICE_COUPLE, coupleId);
  } catch {
    /* Private mode / storage denied: the ceremony just stays in-memory-only,
       which is the old (safe) behaviour. */
  }
}

export function getToken(): string | null {
  return localStorage.getItem(LS_TOKEN);
}
/**
 * The couple's 6-char code, recovered from the SERVER-issued coupleId that
 * saveSession stored (card qntf9Qa0Cemr). `coupleId` is always `c_<code>`
 * (coupleIdFromCode), so the code is a prefix strip — never minted here. The
 * shared-inbox address is one `us@` / `me@` lane on the product domain
 * (shared/inbox-address INBOX_DOMAIN); every surface reads it
 * from this one value so they cannot disagree. null when no couple is joined.
 */
export function getCoupleCode(): string | null {
  const coupleId = localStorage.getItem(LS_COUPLE_ID);
  if (!coupleId || !coupleId.startsWith('c_')) return null;
  const code = coupleId.slice(2);
  return code.length > 0 ? code : null;
}
export function getMemberId(): string | null {
  return localStorage.getItem(LS_MEMBER_ID);
}
/**
 * The account session (card yZexwSigXhw1): the bearer for /api/auth/* plus
 * the couple link the server last reported.
 *
 * `coupleId`/`memberId` are the reconciliation cache. A device can hold a
 * couple token that no longer matches its account (Leave, or a couple that
 * was dissolved server-side) — checking this pair on load tells us whether
 * the stored room token is still this account's, WITHOUT a round trip when
 * it is. The key's ABSENCE means "never asked": written before this card, or
 * a legacy session — the loader then falls back to GET /api/auth/me once and
 * writes the answer back.
 */
export interface AuthSession {
  token: string;
  email: string;
  coupleId?: string | null;
  memberId?: string | null;
}

/** Persist the account session so sign-in survives the tab but not a cleared
 *  browser (clearing wipes both — login re-mints from email+password). */
export function saveAuthSession(auth: AuthSession): void {
  localStorage.setItem(LS_AUTH, JSON.stringify(auth));
  // Card Vn4Up6kLOCc3 — every path that persists an account session (password
  // sign-in, register, the OAuth exchange, a link update) therefore also
  // records who this device belongs to, so recognition outlives the sign-out
  // that deletes the session above. The display name already recorded for
  // this address is kept; a different address replaces it outright.
  if (auth.email) {
    const prev = getLastIdentity();
    const name = prev && prev.email === auth.email ? prev.name : undefined;
    saveLastIdentity(name ? { email: auth.email, name } : { email: auth.email });
  }
}
export function getAuthSession(): AuthSession | null {
  const raw = localStorage.getItem(LS_AUTH);
  if (!raw) return null;
  try {
    const a = JSON.parse(raw) as { token?: string; email?: string } & Partial<AuthSession>;
    if (!a?.token) return null;
    const out: AuthSession = { token: a.token, email: a.email ?? '' };
    if ('coupleId' in a) {
      out.coupleId = a.coupleId ?? null;
      out.memberId = a.memberId ?? null;
    }
    return out;
  } catch {
    return null;
  }
}
/** True when this session already carries a server-reported link (so the
 *  fast path is safe); false for a legacy session that must ask once. */
export function hasAccountLink(a: AuthSession): boolean {
  return 'coupleId' in a;
}
/**
 * Record what the server says about this account's couple — called after
 * login / register / OAuth exchange, after a successful link on create or
 * join, and after Leave clears it. Merges into the stored session so the
 * token and email are never rewritten by a link update.
 */
export function saveAccountLink(link: { coupleId: string | null; memberId?: string | null }): void {
  const a = getAuthSession();
  if (!a) return;
  saveAuthSession({ ...a, coupleId: link.coupleId, memberId: link.memberId ?? null });
}
export function saveMembers(members: Member[]) {
  localStorage.setItem(LS_MEMBERS_JSON, JSON.stringify(members));
}

/**
 * Card iwzh8dGqPxKO — the invite code this device is waiting on, persisted.
 *
 * pendingCode lived only in React state, so a reload of an unpaired creator
 * wiped the invite card (code, copy buttons, QR) and left them on a pair
 * screen with no invite and no way to get it back — the invitation existed
 * only in whatever message they had already sent. It is not a session
 * credential: it cannot pair without an account, and the person holding it is
 * the one who is allowed to hand it out.
 */
export function savePendingCode(code: string | null) {
  if (code) localStorage.setItem(LS_PENDING_CODE, code);
  else localStorage.removeItem(LS_PENDING_CODE);
}
export function loadPendingCode(): string | null {
  return localStorage.getItem(LS_PENDING_CODE);
}

/** The invite this device was SENT (UX review item 3, card 8aiEHiwNjjKH).
 *
 *  Separate key on purpose: `pear_pending_code` is the code a creator OWNS and
 *  hands out — reusing it for the recipient would paint the creator's "share
 *  this code" hero on the partner's screen, showing them a code they do not
 *  own. This one is what arrived in a link, so the pair screen can name the
 *  couple being joined, prefill the field again after a reload, and hold the
 *  code across the Google sign-in round trip (which leaves the app and comes
 *  back through the worker with no query of its own).
 *
 *  Not a credential: it cannot pair without an account, it is the value the
 *  holder was already handed in the open, and it dies with the invite. */
const LS_INVITED_CODE = 'pear_invited_code';

export function saveInvitedCode(code: string | null) {
  if (code) localStorage.setItem(LS_INVITED_CODE, code);
  else localStorage.removeItem(LS_INVITED_CODE);
}
export function loadInvitedCode(): string | null {
  return localStorage.getItem(LS_INVITED_CODE);
}

/**
 * Card tJdisOEkVwha — the emailed 24h invite LINK, as it arrived on
 * ?invite=… It is NOT a credential this app can check offline (only the
 * worker can verify the signature and the single-use ledger), so it is kept
 * as an opaque string and handed straight back to /api/pair/join-link.
 */
const LS_INVITED_LINK = 'pear_invited_link';

export function saveInvitedLink(link: string | null) {
  if (link) localStorage.setItem(LS_INVITED_LINK, link);
  else localStorage.removeItem(LS_INVITED_LINK);
}
export function loadInvitedLink(): string | null {
  return localStorage.getItem(LS_INVITED_LINK);
}

/** POST /api/pair/join-link — join through an emailed link (single-use). */
export async function joinWithLinkRequest(link: string, displayName: string) {
  const a = getAuthSession();
  return api<{
    couple: { coupleId: string; code: string };
    handle?: string | null;
    memberId: string;
    role: Role;
    token: string;
    justPaired?: boolean;
  }>('/api/pair/join-link', {
    body: { link, displayName },
    headers: a?.token ? { Authorization: `Bearer ${a.token}` } : {},
  });
}

/** GET /api/pair/link-info?link=… — is this link still good, and whose is it?
 *  Readable before sign-in on purpose: the join screen names the couple. */
export async function inviteLinkInfo(link: string) {
  return api<{ ok: boolean; reason: string | null; handle: string | null; inviterName: string | null; expiresAt: number | null }>(
    `/api/pair/link-info?link=${encodeURIComponent(link)}`,
  );
}
export function loadMembers(): Member[] {
  const raw = localStorage.getItem(LS_MEMBERS_JSON) ?? '';
  if (!raw) return [];
  try {
    return JSON.parse(raw) as Member[];
  } catch {
    return [];
  }
}
export function saveSession(token: string, coupleId: string, role: Role, memberId: string) {
  localStorage.setItem(LS_TOKEN, token);
  localStorage.setItem(LS_COUPLE_ID, coupleId);
  localStorage.setItem(LS_ROLE, role);
  localStorage.setItem(LS_MEMBER_ID, memberId);
}
export function clearSession() {
  localStorage.removeItem(LS_TOKEN);
  localStorage.removeItem(LS_COUPLE_ID);
  localStorage.removeItem(LS_ROLE);
  localStorage.removeItem(LS_MEMBER_ID);
  localStorage.removeItem(LS_MEMBERS_JSON);
  localStorage.removeItem(LS_AUTH);
  localStorage.removeItem(LS_PENDING_CODE);
}
/**
 * Card Vn4Up6kLOCc3 — WHO this device was, after the session is gone.
 *
 * Sign-out clears `pear_auth_session`, so the landing screen had no idea who
 * had just left: sign out, come back later, and the app asks for an address
 * as if nobody had ever been here. This is recognition WITHOUT authority —
 * an email and a display name only, never a token, never a couple link, and
 * never enough to open anything. Written by `useSession.leave()` from the
 * session being dropped; dropped by `clearLastIdentity()` ("Not you?") and
 * overwritten by the next sign-out, which records whoever left after them.
 */
const LS_IDENTITY = 'pear_last_identity';

export interface LastIdentity {
  email: string;
  /** The member's display name when the sign-out happened inside a couple. */
  name?: string;
}

export function saveLastIdentity(id: LastIdentity): void {
  const email = id.email.trim();
  if (!email) return;
  localStorage.setItem(LS_IDENTITY, JSON.stringify(id.name ? { email, name: id.name } : { email }));
}
export function getLastIdentity(): LastIdentity | null {
  try {
    const raw = localStorage.getItem(LS_IDENTITY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<LastIdentity>;
    if (!parsed?.email) return null;
    return parsed.name ? { email: parsed.email, name: parsed.name } : { email: parsed.email };
  } catch {
    return null;
  }
}
export function clearLastIdentity(): void {
  localStorage.removeItem(LS_IDENTITY);
}
/**
 * Card Vf23eSKKxDhX — drop the COUPLE from this device and keep the ACCOUNT.
 *
 * Leave is not sign out (Jonathan email 2026-10-07: "there's no sign out only
 * leave"): the person is walking away from a pairing, not from Pear. The auth
 * session survives so the pair screen they land on can offer Create and Join
 * straight away — both of which need the account bearer, and neither of which
 * should demand a re-type of an email and password the device already holds.
 */
export function clearCoupleSession() {
  /* UX item 5: the session-scoped UI bag (a half-written message, a filter, a
     scroll offset) belongs to the couple whose screen it was typed on, so it
     goes with the couple session — never into the next person's window. */
  forgetAll();
  localStorage.removeItem(LS_TOKEN);
  localStorage.removeItem(LS_COUPLE_ID);
  localStorage.removeItem(LS_ROLE);
  localStorage.removeItem(LS_MEMBER_ID);
  localStorage.removeItem(LS_MEMBERS_JSON);
  localStorage.removeItem(LS_PENDING_CODE);
}
/**
 * POST /api/pair/leave — the server half of Leave (card Vf23eSKKxDhX).
 *
 * The ACCOUNT bearer is the credential, not the couple token: leaving is an
 * account-level decision about which couple it belongs to, and the couple
 * token is exactly the access being given up — a request should never be able
 * to end its own authority by presenting it. Throws the usual
 * `{error:{code,message}}` Error on refusal; `authErrorMessage` decodes it.
 */
export async function leaveCoupleRequest(): Promise<{ success: boolean; closed: boolean; membersRemaining: number }> {
  return authApi('/api/pair/leave', { method: 'POST' });
}
/**
 * GET /api/export/me — this member's private half of the couple as a download
 * bundle (card ocJz1TeLT4pi). Account bearer, same as leave. Returns an
 * ExitBundle; the caller serialises it for the user to save.
 */
export async function exportMeRequest(): Promise<Record<string, unknown>> {
  return authApi('/api/export/me', { method: 'GET' });
}
/**
 * POST /api/pair/import — re-file a previously exported bundle into the
 * CURRENT couple (card O2J0Cm1ZFeNd). Account bearer.
 */
export async function importBundleRequest(bundle: Record<string, unknown>): Promise<{ success: boolean; imported: number }> {
  return authApi('/api/pair/import', { body: bundle });
}
export function sessionIsRestorable(): boolean {
  return Boolean(getToken() && localStorage.getItem(LS_COUPLE_ID) && localStorage.getItem(LS_ROLE));
}

/* ---- Account auth (DB-backed login; epic d1Zu9d4KosuX) ---- */

export interface AuthAccount {
  id: string;
  email: string;
  name: string | null;
  displayName: string | null;
  handle: string | null;
  coupleId: string | null;
  memberId: string | null;
  emailVerified: boolean;
}

export interface AuthLoginResult {
  token: string;
  account: AuthAccount;
  /** Present when the account is already linked to a couple: the caller can
   *  saveSession() straight into the room (cleared-browser recovery). */
  coupleToken?: string | null;
}

const authHeaders = (): Record<string, string> => {
  const auth = getAuthSession();
  const t = auth?.token;
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * System-failure plumbing (Jonathan 2026-10-24).
 *
 * Two things used to reach the person raw: a transport failure ("Failed to
 * fetch") and a body that is not JSON at all — Pages answers an unknown /api
 * path with the SPA's index.html, so `res.json()` threw "Unexpected token
 * '<'" and the app read as broken with no explanation. Both are OUR failure,
 * so both come back as the support line with a short reference the person can
 * quote in a mail. Validation failures (4xx with our own JSON) are untouched.
 */
function failWith(message: string, code: string): never {
  throw new Error(JSON.stringify({ error: { code, message } }));
}

/** ONE place a request is made, so no call site can leak a browser string. */
async function request(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch {
    failWith(GRACEFUL_FAILURE, 'network');
  }
}

/** Read a body as JSON, or fail as an issue rather than a parse error. */
async function readJson(res: Response): Promise<any> {
  const text = await res.text();
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    failWith(GRACEFUL_FAILURE, `bad_response_${res.status}`);
  }
}

/** Auth-surface fetch: uses the ACCOUNT session token, not the couple token.
 *  `method` defaults to POST when a body is given — PUT /api/auth/profile is
 *  the one route that needs the other verb (card yFLCjm7ONIEc). */
export async function authApi<T = any>(
  path: string,
  opts?: { body?: Record<string, unknown>; headers?: Record<string, string>; method?: 'GET' | 'POST' | 'PUT' },
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(opts?.headers ?? {}) };
  const res = await request(API_BASE + path, {
    method: opts?.body ? (opts?.method ?? 'POST') : (opts?.method ?? 'GET'),
    headers: { ...headers, ...authHeaders() },
    body: opts?.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await readJson(res);
  if (!res.ok) throw new Error(JSON.stringify(data));
  return data as T;
}

export async function api<T = any>(
  path: string,
  opts?: { body?: Record<string, unknown>; headers?: Record<string, string>; method?: 'GET' | 'POST' | 'DELETE' },
): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json', ...(opts?.headers ?? {}) };
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await request(API_BASE + path, {
    // `method` is opt-in so every existing caller keeps its GET/POST inference;
    // DELETE (vault rows, card t193KwD1i7aY) is the first verb a screen needs
    // that a body cannot express.
    method: opts?.method ?? (opts?.body ? 'POST' : 'GET'),
    headers,
    body: opts?.body ? JSON.stringify(opts.body) : undefined,
  });
  const data = await readJson(res);
  if (!res.ok) {
    // Pear Plus (card 32BEnNO6I2kx): 402 plus_required is the one error the
    // app answers with a PAYWALL rather than a message. Announce it globally
    // so the upgrade sheet opens wherever the tap happened, then still throw
    // so the caller's own error handling stays honest.
    if (res.status === 402 && (data as { error?: { code?: string } })?.error?.code === 'plus_required') {
      window.dispatchEvent(new CustomEvent('pear:plus-required'));
    }
    // A 5xx is our outage, not a message the person can act on (2026-10-24).
    if (res.status >= 500) failWith(GRACEFUL_FAILURE, `server_${res.status}`);
    throw new Error(JSON.stringify(data));
  }
  return data as T;
}

/* ---- Google sign-in (epic d1Zu9d4KosuX Phase B) ---- */

export interface AuthConfig {
  passwordLogin: boolean;
  oauth: boolean;
  verifyEmail: boolean;
  forgotPassword: boolean;
  /** PEAR_SIGNUP_MODE is open. False means the worker refuses new accounts, so
   *  the create path must not be offered (the server check is the real gate). */
  signups: boolean;
  /** Card J9hHjSZDFTCV — PEAR_PRESENCE on the worker. Consumers require
   *  `=== true` (never a truthy check): a missing field, null or false all
   *  mean the glow stays off, so an old worker or a failed config fetch can
   *  never light a header the server is not feeding. */
  presence?: boolean;
  /** Card tPPB3ozS4YrK — PEAR_TYPING on the worker, mirrored with the same
   *  `=== true` rule as presence: no field, null or false all mean the
   *  indicator stays out of the DOM, so a client can never show a hint the
   *  room is not relaying. */
  typing?: boolean;
}

/** A stalled request must not hang everything behind it — a config call that
 *  neither answers nor fails is exactly the state the founder was stuck in. */
const AUTH_CONFIG_ATTEMPT_MS = 5000;

/** What the worker can actually do — the UI only offers what is wired, so a
 *  missing secret hides the button instead of failing the click.
 *
 *  Card SSnGE04ShOvB: ONE attempt, bounded by a deadline as well as by a
 *  rejection. Any failure is a miss, never a throw — when to ask again is the
 *  caller's call, because "give up" used to mean "the Google button is gone
 *  until this page is refreshed". */
export async function fetchAuthConfig(): Promise<AuthConfig | null> {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), AUTH_CONFIG_ATTEMPT_MS);
  try {
    const res = await fetch(API_BASE + '/api/auth/config', { signal: ctrl.signal });
    if (!res.ok) return null;
    return (await res.json()) as AuthConfig;
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

/** Full-page bounce to the worker, which sets the state cookie and 302s on
 *  to Google. `return` is this origin — the callback allow-lists it before
 *  it will redirect anywhere. */
export function oauthStartUrl(): string {
  return `${API_BASE}/api/auth/oauth/google?return=${encodeURIComponent(window.location.origin)}`;
}

/** One-time code from the callback -> session (same shape as /login). */
export async function exchangeOAuthCode(code: string): Promise<AuthLoginResult> {
  return authApi<AuthLoginResult>('/api/auth/oauth/exchange', { body: { code } });
}

/* ---- Accounts mail: confirm email + reset password (Sprint 2026-10-06) ---- */

export interface MailResponse {
  success: boolean;
  /** Whether the worker handed the message to the transport (false = mail not
   *  configured — the UI hides these controls anyway when config says so). */
  sent: boolean;
  /** Development only: the one-time link, since no mail leaves the machine. */
  devLink?: string;
}

/** Ask for a confirmation link. Address omitted -> the signed-in account. */
export async function requestVerifyEmail(email?: string): Promise<MailResponse> {
  return authApi<MailResponse>('/api/auth/verify/request', { body: { email: email ?? '' } });
}

/** Exchange the one-time token from the mail link for a confirmed address. */
export async function confirmVerifyEmail(token: string): Promise<{ account: AuthAccount }> {
  return authApi<{ account: AuthAccount }>('/api/auth/verify', { body: { token } });
}

/** Ask for a reset link. Same shape as requestVerifyEmail — the API answers
 *  identically whether or not the address exists (no account enumeration). */
export async function requestPasswordReset(email: string): Promise<MailResponse> {
  return authApi<MailResponse>('/api/auth/forgot', { body: { email } });
}

/** Burn the reset token and set the new password. Revokes every session the
 *  old password opened. */
export async function resetPassword(token: string, password: string): Promise<{ success: boolean }> {
  return authApi<{ success: boolean }>('/api/auth/reset', { body: { token, password } });
}

/** One readable line from an authApi() rejection ({error:{code,message}}). */
export function authErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message.startsWith('{')) {
    try {
      const parsed = JSON.parse(e.message) as { error?: { message?: string } };
      if (parsed.error?.message) return parsed.error.message;
    } catch {
      /* fall through to the caller's wording */
    }
  }
  return fallback;
}

/** WebSocket endpoint that matches API_BASE (http→ws, same host in dev). */
export function wsUrl(token: string): string {
  const wsBase = API_BASE
    ? API_BASE.replace(/^http/, 'ws')
    : `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}`;
  return `${wsBase}/ws?token=${encodeURIComponent(token)}`;
}

/* ── Decisions (parent card VcBi3nr9GjBj, child B HMzNgJETH7HE) ──────────
   The Thread card owns its own fetch, same hand-off as requests/chores: the
   `decisions` websocket frame carries an id and the client re-reads its OWN
   projection, so what a partner picked never travels in the broadcast. */

/** The sealed decision shape — shared/decisions.ts is the single definition. */
export type { DecisionChoice, DecisionView } from '../../../shared/decisions';

/** Every decision for this couple, already sealed for the caller. */
export async function listDecisions(): Promise<{ decisions: any[] }> {
  return api('/api/decisions');
}

/** Arm a decision from the typed message the chip sat on. `sourceSeq` is what
 *  makes the bubble become the card — the room points messages.round_id at the
 *  decision and refuses anyone else's message (card HMzNgJETH7HE). */
export async function createDecision(body: {
  title: string;
  optionA: string;
  optionB: string;
  initiationSource?: string;
  sourceSeq?: number;
  /** Agent-proposed options 3..n — Plus only; the server answers plus_required. */
  extraOptions?: string[];
}): Promise<any> {
  return api('/api/decisions', { body });
}

/** One pick. The response is the caller's own sealed view; the partner learns
 *  from the `decisions` frame, not from this return value. */
export async function voteDecision(decisionId: string, choice: 'a' | 'b'): Promise<any> {
  return api(`/api/decisions/${encodeURIComponent(decisionId)}/vote`, { body: { choice } });
}

/** Withdraw a decision you raised (card jL8e2DEkr0S0). Author-only: the room
 *  answers 403 forbidden for the partner, and the votes leave with the row — the
 *  rail and Today both reload from the id-only `decisions` frame the delete
 *  broadcasts, so no caller has to patch local state. */
export async function deleteDecision(decisionId: string): Promise<{ decisionId: string; deleted: boolean }> {
  return api(`/api/decisions/${encodeURIComponent(decisionId)}`, { method: 'DELETE' });
}
